import { getEventsInitialState, eventStateByStatus } from './config';
import dayjs from 'dayjs';
import { DayjsFormat } from '../../helpers/date-time';
import {
  CalendarEventStatus,
  CalendarCateringEvent,
  CalendarPopupEvent,
  CalendarDeliveryEvent,
  TEventDetails,
  TCalendarEvent,
  EventTypeFilter,
  CalendarEventNode,
  EventInfo,
} from './types';
import { EventProduct, EventStatus } from '@/api';
import { DeliveryEventStatus } from '../Event.page/types';
import { MantineTheme } from '@mantine/core';
import { getVendorIds } from '@/helpers';
import { TSearchEvent } from '../Dashboard.page/types';
import { aggregateDeliveryEventVendors } from '../Dashboard.page/helpers';

export const getEventsCalendarData = ({
  events,
  startDate,
  endDate,
  theme,
}: {
  events: CalendarEventNode[];
  startDate: string;
  endDate: string;
  theme: MantineTheme;
}) => {
  const eventsByDay = new Map();

  // Create a map entry for each day from startDate to endDate
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const totalDays = end.diff(start, 'day');

  for (let i = 0; i <= totalDays; i++) {
    const currentDate = start.add(i, 'day');
    const dateKey = currentDate.format(DayjsFormat.CALENDAR_DATE);
    eventsByDay.set(dateKey, structuredClone(getEventsInitialState(theme)));
  }

  const aggregatedEvents = aggregateDeliveryEventVendors(
    events.filter((event): event is NonNullable<CalendarEventNode> => event !== null)
  );

  const preparedEvents = aggregatedEvents.reduce((acc, event) => {
    const eventDetails = getEventDetails(event);
    const eventDate = dayjs(eventDetails?.startTime).format(DayjsFormat.CALENDAR_DATE);
    const eventDay = acc.get(eventDate);
    const productTypeKey = event?.productType.toLowerCase();

    if (!eventDay || !event || !productTypeKey) return acc;

    const eventsByProductType = eventDay[productTypeKey];

    // Get the initial status grade before processing vendors
    let currentEventsStatusGrade = eventsByProductType.statusGrade;
    const calendarEventStatus = getCalendarEventStatus({
      status: eventDetails?.status.toUpperCase(),
      event,
    });

    eventsByProductType.count++;

    const eventsSettingByStatus = getEventsSettingByStatus(
      currentEventsStatusGrade,
      calendarEventStatus
    );

    const eventInfo: EventInfo = {
      id: event.id,
      accountName: getAccountName(event),
      status: calendarEventStatus,
      startTime: eventDetails?.startTime,
    };

    eventsByProductType.events.push(eventInfo);

    // Update status grade if needed
    if (eventsSettingByStatus) {
      Object.assign(eventsByProductType, eventsSettingByStatus);
      // Update the tracking variable so next vendor uses the new grade
      currentEventsStatusGrade = eventsSettingByStatus.statusGrade;
    }

    acc.set(eventDate, {
      ...acc.get(eventDate),
      [productTypeKey]: eventsByProductType,
    });

    return acc;
  }, eventsByDay);

  return preparedEvents;
};

export const getCalendarEventStatus = ({
  event,
  status,
}: {
  event: TCalendarEvent | TSearchEvent;
  status?: string;
}) => {
  if (!event) return CalendarEventStatus.NOT_READY;

  if (status === EventStatus.COMPLETE || status === EventStatus.FINAL) {
    return CalendarEventStatus.COMPLETED;
  }

  //CATERING
  if (event.productType === EventProduct.CATERING) {
    if (status === EventStatus.ACTIVE) {
      return CalendarEventStatus.READY;
    }
    // Any other status for catering events is scheduled
    return CalendarEventStatus.SCHEDULED;
  }

  // POPUP,CAFE
  if (event.productType === EventProduct.POPUP || event.productType === EventProduct.CAFE) {
    const eventConfirmed = (event as CalendarPopupEvent).confirmed;
    const siteDirectionSent = (event as CalendarPopupEvent).siteDirectionsEmailSent;
    if (eventConfirmed) {
      return CalendarEventStatus.READY;
    }
    if (!eventConfirmed && !siteDirectionSent) {
      return CalendarEventStatus.SCHEDULED;
    }

    if (!eventConfirmed) {
      return CalendarEventStatus.NOT_READY;
    }
  }

  // DELIVERY
  if (event.productType === EventProduct.DELIVERY) {
    const deliveryEvent = event as CalendarDeliveryEvent;
    if (deliveryEvent.foodDelivered) {
      return CalendarEventStatus.COMPLETED;
    }
    const preManifestDocument = deliveryEvent.preManifestDocument;
    const finalManifestDocument = deliveryEvent.finalManifestDocument;
    const preManifestDocumentConfirmed =
      preManifestDocument?.status === DeliveryEventStatus.CONFIRMED;
    const finalManifestDocumentConfirmed =
      finalManifestDocument?.status === DeliveryEventStatus.CONFIRMED;

    // No manifest documents
    if (!preManifestDocument && !finalManifestDocument) {
      return CalendarEventStatus.SCHEDULED;
    }

    // Preview manifest confirmed and final manifest not initialized
    if (preManifestDocument && !finalManifestDocument && preManifestDocumentConfirmed) {
      return CalendarEventStatus.READY;
    }
    // Final manifest confirmed and preview manifest not initialized
    if (!preManifestDocument && finalManifestDocument && finalManifestDocumentConfirmed) {
      return CalendarEventStatus.READY;
    }
    // Preview manifest confirmed and final manifest confirmed
    if (
      preManifestDocument &&
      finalManifestDocument &&
      preManifestDocumentConfirmed &&
      finalManifestDocumentConfirmed
    ) {
      return CalendarEventStatus.READY;
    }

    // Other cases return not ready
    // Preview manifest initialized but not confirmed  or final manifest initialized but not confirmed
    // Preview manifest initialized but not confirmed and final manifest initialized but not confirmed
    return CalendarEventStatus.NOT_READY;
  }
  return CalendarEventStatus.NOT_READY;
};

const getEventsSettingByStatus = (
  currentEventsStatusGrade: number,
  eventStatus: CalendarEventStatus
) => {
  const eventSettings = eventStateByStatus[eventStatus];

  // Check if we have a mix of ready (2) and not_ready (3) statuses
  const hasReadyAndNotReady =
    (currentEventsStatusGrade === 2 && eventSettings.statusGrade === 3) ||
    (currentEventsStatusGrade === 3 && eventSettings.statusGrade === 2);

  if (hasReadyAndNotReady) {
    return eventStateByStatus.partially_ready;
  }

  // Only update if the new status has a higher grade
  if (eventSettings.statusGrade > currentEventsStatusGrade) {
    return eventSettings;
  }

  return null;
};

export const getEventDetails = (event: CalendarEventNode): TEventDetails | null => {
  if (!event) return null;

  switch (event.productType) {
    case EventProduct.POPUP:
    case EventProduct.CAFE:
      return (event as CalendarPopupEvent).popupEvent;
    case EventProduct.DELIVERY:
      return (event as CalendarDeliveryEvent).selectEvent;
    case EventProduct.CATERING:
      return (event as CalendarCateringEvent).event;
    default:
      return null;
  }
};

const getAccountName = (event: CalendarEventNode) => {
  if (event?.productType === EventProduct.DELIVERY) {
    return '';
  }
  if (event?.productType === EventProduct.CATERING) {
    return (event as CalendarCateringEvent)?.event?.name || '';
  }
  if (event?.productType === EventProduct.POPUP || event?.productType === EventProduct.CAFE) {
    return (event as CalendarPopupEvent)?.popupEvent?.account?.name || '';
  }
  return '';
};

export const calculateDateBoundary = (
  currentDate: string,
  today: string,
  boundaryType: 'startOf' | 'endOf',
  threshold: string,
  thresholdComparison: 'before' | 'after',
  offsetDirection: 'subtract' | 'add',
  offsetDays = 6
): string => {
  const isPastDate = dayjs(currentDate).isBefore(today, 'day');
  const baseDate = isPastDate
    ? dayjs(currentDate)[boundaryType]('month')
    : dayjs(today)[boundaryType]('month');

  const dateWithOffset = baseDate[offsetDirection](offsetDays, 'day');
  const thresholdDate = dayjs(threshold);

  const exceedsThreshold =
    thresholdComparison === 'before'
      ? dateWithOffset.isBefore(thresholdDate)
      : dateWithOffset.isAfter(thresholdDate);

  return exceedsThreshold ? baseDate.format() : dateWithOffset.format();
};

export const getFilteredEvents = (
  events: CalendarEventNode[],
  selectedVendorIds: string[],
  selectedEventType: EventTypeFilter[]
) => {
  const vendorIds = getVendorIds();
  return events
    .filter((event) => {
      if (event?.productType === EventProduct.POPUP || event?.productType === EventProduct.CAFE) {
        return (
          (event as CalendarPopupEvent).popupEvent.status.toUpperCase() !== EventStatus.SCHEDULED
        );
      }
      if (event?.productType === EventProduct.DELIVERY) {
        return (event as CalendarDeliveryEvent).selectEvent.status !== EventStatus.SCHEDULED;
      }
      return true;
    })
    .filter(
      (event) =>
        !selectedEventType.some((type) => type.label === event?.productType && type.value === false)
    )
    .filter((event) => {
      return selectedVendorIds.length === vendorIds.length
        ? true
        : selectedVendorIds.includes(event?.vendor?.id || '');
    });
};

export const getEventBorderColor = (eventType: EventProduct, theme: MantineTheme): string => {
  const state = getEventsInitialState(theme);
  switch (eventType) {
    case EventProduct.POPUP:
      return state.popup.bg;
    case EventProduct.CAFE:
      return state.cafe.bg;
    case EventProduct.DELIVERY:
      return state.delivery.bg;
    case EventProduct.CATERING:
      return state.catering.bg;
  }
};
